import type { SVGProps } from 'react'
import type { SiteSettings } from '@/lib/types'

/**
 * Brand marks as inline SVG. lucide-react v1 removed its brand icon set, and
 * these glyphs are trademarks best drawn explicitly rather than pulled from a
 * general icon pack that may rename or drop them again.
 *
 * All paths are drawn on a 24×24 grid and inherit `currentColor`.
 */
type IconProps = SVGProps<SVGSVGElement>

const base = (props: IconProps) => ({
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true as const,
  ...props,
})

export const InstagramIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38c-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 0 0-2.13 1.38A5.9 5.9 0 0 0 .63 4.14c-.3.76-.5 1.64-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91a5.9 5.9 0 0 0 1.38 2.13 5.9 5.9 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a6.1 6.1 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Z" />
    <path d="M12 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32Zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" />
    <circle cx="18.41" cy="5.59" r="1.44" />
  </svg>
)

export const FacebookIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
  </svg>
)

export const YoutubeIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.08 0 12 0 12s0 3.92.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.92 24 12 24 12s0-3.92-.5-5.81ZM9.55 15.57V8.43L15.82 12l-6.27 3.57Z" />
  </svg>
)

export const TiktokIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M21 8.29a6.72 6.72 0 0 1-4.19-1.45v6.6a5.85 5.85 0 1 1-5.05-5.8v3.32a2.67 2.67 0 1 0 1.87 2.55V0h3.18a3.55 3.55 0 0 0 .05.65 3.58 3.58 0 0 0 1.58 2.35A3.55 3.55 0 0 0 21 3.68v4.61Z" />
  </svg>
)

export const XIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M18.24 2h3.31l-7.23 8.26L22.82 22h-6.65l-5.21-6.82L4.99 22H1.68l7.73-8.84L1.18 2h6.82l4.71 6.23L18.24 2Zm-1.16 18h1.83L7.01 3.9H5.04L17.08 20Z" />
  </svg>
)

export const WhatsappIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.38-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35Z" />
    <path d="M12.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.13 1.58 5.93L0 24l6.38-1.67a11.8 11.8 0 0 0 5.66 1.44h.01c6.54 0 11.84-5.3 11.84-11.84C23.89 5.3 18.58 0 12.04 0Zm0 21.71h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.21-3.72.98.99-3.63-.23-.37a9.8 9.8 0 0 1-1.5-5.23c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.1 1.03 6.96 2.89a9.78 9.78 0 0 1 2.88 6.97c0 5.43-4.42 9.85-9.85 9.85Z" />
  </svg>
)

/** Which settings fields map to which mark, in the order they should render. */
export const SOCIAL_LINKS = [
  { key: 'instagram', label: 'Instagram', Icon: InstagramIcon },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon },
  { key: 'tiktok', label: 'TikTok', Icon: TiktokIcon },
  { key: 'youtube', label: 'YouTube', Icon: YoutubeIcon },
  { key: 'x_twitter', label: 'X', Icon: XIcon },
  { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsappIcon },
] as const satisfies readonly {
  key: keyof SiteSettings
  label: string
  Icon: (props: IconProps) => React.ReactElement
}[]
